import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, arrayUnion, orderBy, deleteDoc, addDoc, serverTimestamp, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { User } from '../types';
import { isTodayBirthday } from '../utils/ageCalculator';
import { loadAllUserBadges } from '../utils/badgeCalculator';
import { badgeDefinitions, getBadgeDefinition } from '../utils/badgeDefinitions';
import NotificationSystem from './NotificationSystem';
import BirthdayCelebration from './BirthdayCelebration';
import DailyPracticeGoalCard from './DailyPracticeGoalCard';
import AvatarDisplay from './AvatarDisplay';

interface PracticeSession {
  id: string;
  studentId: string;
  lessonId: string;
  plannedTotalMinutes: number;
  plannedDurations: Record<string, number>;
  actualDurations: Record<string, number>;
  completed: boolean;
  startAt: any;
  endAt?: any;
  durationSec?: number;
  taskCounts: Record<string, number>;
  createdAt: any;
}

interface FeedEvent {
  id: string;
  type: 'heart_sent' | 'daily_goal_reached' | 'weekly_goal_reached' | 'badge_earned' | 'practice_reminder';
  fromUserId: string;
  fromUserNickname: string;
  toUserId?: string; // For heart/nudge events
  toUserNickname?: string; // For heart/nudge events
  badgeName?: string; // For badge events
  timestamp: any;
  hearts: string[]; // Users who sent hearts to this event
  nudges: string[]; // Users who sent nudges to this event
}

const Dashboard: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [refreshedUser, setRefreshedUser] = useState<User | null>(currentUser);
  const [loading, setLoading] = useState(true);
  const [linkedTeacherName, setLinkedTeacherName] = useState<string>('');
  const [latestLessonQuotes, setLatestLessonQuotes] = useState<string[]>([]);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  
  // Feed data state
  const [feedEvents, setFeedEvents] = useState<FeedEvent[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [buddies, setBuddies] = useState<any[]>([]);
  const [usersStartedPractice, setUsersStartedPractice] = useState<Set<string>>(new Set());
  const [sentHearts, setSentHearts] = useState<Set<string>>(new Set());
  const [sentNudges, setSentNudges] = useState<Set<string>>(new Set());
  
  // Carousel state
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [carouselInterval, setCarouselInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Daily Practice Goal state
  const [earnedBadges, setEarnedBadges] = useState<string[]>([]);
  const [badgesLoaded, setBadgesLoaded] = useState(false);
  const [weeklyCompletedSessions, setWeeklyCompletedSessions] = useState<any[]>([]);
  const [allWeeklySessions, setAllWeeklySessions] = useState<any[]>([]);
  const [weeklyProgressLoaded, setWeeklyProgressLoaded] = useState(false);
  const [showBirthdayCelebration, setShowBirthdayCelebration] = useState(false);
  const [hasBirthdayBadge, setHasBirthdayBadge] = useState(false);
  const [currentSessionProgress, setCurrentSessionProgress] = useState(0); // Progress percentage (0-100)
  const [isPracticeSessionActive, setIsPracticeSessionActive] = useState(false);
  
  // Badge celebration state
  const [showBadgeCelebration, setShowBadgeCelebration] = useState(false);
  const [newBadge, setNewBadge] = useState<{ id: string; name: string; description: string; emoji: string; color: string } | null>(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  // Load user's buddies
  const loadBuddies = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDocs(query(collection(db, 'users'), where('linkedBuddies', 'array-contains', currentUser.uid)));
      const buddyData = userDoc.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      setBuddies(buddyData);
    } catch (error) {
      console.error('Error loading buddies:', error);
    }
  };

  // Load feed events
  const loadFeedEvents = async () => {
    setLoadingFeed(true);
    try {
      if (!currentUser) {
        setFeedEvents([]);
        return;
      }

      // Get current user's buddy linking timestamps
      const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const currentUserData = currentUserDoc.data();
      const buddyLinkedAt = currentUserData?.buddyLinkedAt || {};

      // Try to load feed events that the current user can see
      // First try with buddies, then fallback to user's own events
      let events: FeedEvent[] = [];
      
      if (buddies.length > 0) {
        try {
          const buddyIds = buddies.map(buddy => buddy.uid);
          
          // Load events for each buddy individually to avoid index requirements
          const allEvents: FeedEvent[] = [];
          
          for (const buddyId of buddyIds.slice(0, 5)) { // Limit to 5 buddies to avoid too many queries
            try {
              const buddyFeedQuery = query(
                collection(db, 'feedEvents'),
                where('fromUserId', '==', buddyId),
                limit(50) // Increased limit since we'll filter by date client-side
              );

              const buddyFeedSnapshot = await getDocs(buddyFeedQuery);
              const buddyEvents = buddyFeedSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              })) as FeedEvent[];
              
              // Filter for last 7 days and buddy linking time
              const sevenDaysAgo = new Date();
              sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
              
              const filteredBuddyEvents = buddyEvents.filter(event => {
                const eventTime = event.timestamp?.toDate?.() || new Date(event.timestamp || 0);
                
                // Skip events older than 7 days
                if (eventTime < sevenDaysAgo) {
                  return false;
                }
                
                // Filter events based on buddy linking time
                const buddyLinkedTime = buddyLinkedAt[buddyId];
                if (buddyLinkedTime) {
                  const linkedTime = buddyLinkedTime.toDate?.() || new Date(buddyLinkedTime);
                  return eventTime >= linkedTime; // Only show events after linking
                }
                return true; // If no linking time, show all events (fallback)
              });
              
              allEvents.push(...filteredBuddyEvents);
            } catch (buddyError) {
              // Silently handle buddy errors
            }
          }
          
          // Filter out heart_sent events to prevent endless loops
          // Also filter out practice reminders where the current user is the target (users don't nudge themselves)
          // And filter out badge awards where the current user is the recipient (they already see the celebration popup)
          const filteredEvents = allEvents.filter(event => 
            event.type !== 'heart_sent' && 
            !(event.type === 'practice_reminder' && event.toUserId === currentUser.uid) &&
            !(event.type === 'badge_earned' && event.fromUserId === currentUser.uid)
          );
          
          // Sort all events by timestamp and take the most recent
          events = filteredEvents
            .sort((a, b) => {
              const aTime = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : new Date(a.timestamp).getTime();
              const bTime = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : new Date(b.timestamp).getTime();
              return bTime - aTime;
            })
            .slice(0, 10);

        } catch (buddyError) {
          // Silently handle buddy feed errors
        }
      }

      // If no events from buddies or error, try user's own events
      if (events.length === 0) {
        try {
          const userFeedQuery = query(
            collection(db, 'feedEvents'),
            where('fromUserId', '==', currentUser.uid),
            limit(20) // Get more events since we're not ordering in query
          );

          const userFeedSnapshot = await getDocs(userFeedQuery);
          const userEvents = userFeedSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as FeedEvent[];

          // Sort user events by timestamp and take the most recent
          events = userEvents
            .sort((a, b) => {
              const aTime = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : new Date(a.timestamp).getTime();
              const bTime = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : new Date(b.timestamp).getTime();
              return bTime - aTime;
            })
            .slice(0, 10);

        } catch (userError) {
          // Silently handle user feed errors
        }
      }

      setFeedEvents(events);
    } catch (error) {
      console.error('Error loading feed events:', error);
      setFeedEvents([]);
    } finally {
      setLoadingFeed(false);
    }
  };

  // Check for missed practice sessions and create nudge cards
  // Check for upcoming practice reminders (15 minutes before scheduled time)
  const checkUpcomingPracticeReminders = async () => {
    if (!currentUser) return;

    try {
      console.log('Checking for upcoming practice reminders...');
      
      // Get current user's today's session
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();
      
      if (!userData?.todayPracticeSession?.time) {
        console.log('No practice session scheduled for today');
        return;
      }

      const scheduledTime = new Date(userData.todayPracticeSession.time);
      const now = new Date();
      const fifteenMinutesBefore = new Date(scheduledTime.getTime() - 15 * 60 * 1000);
      
      console.log(`Scheduled time: ${scheduledTime.toLocaleString()}`);
      console.log(`Reminder time: ${fifteenMinutesBefore.toLocaleString()}`);
      console.log(`Current time: ${now.toLocaleString()}`);
      
      // Check if it's time for the reminder (within a 5-minute window)
      const timeDiff = now.getTime() - fifteenMinutesBefore.getTime();
      const isReminderTime = timeDiff >= 0 && timeDiff < 5 * 60 * 1000; // 5-minute window
      
      if (!isReminderTime) {
        console.log('Not yet time for practice reminder');
        return;
      }

      // Check if user has already started practice today
      const hasStarted = await hasUserStartedPracticeToday(currentUser.uid);
      if (hasStarted) {
        console.log('User has already started practice today, skipping reminder');
        return;
      }

      // Check if reminder notification already exists
      const existingReminderQuery = query(
        collection(db, 'notifications'),
        where('toUserId', '==', currentUser.uid),
        where('type', '==', 'practice_reminder'),
        where('timestamp', '>=', new Date(`${now.toISOString().split('T')[0]}T00:00:00`)),
        where('timestamp', '<=', new Date(`${now.toISOString().split('T')[0]}T23:59:59`))
      );
      
      const existingReminderSnapshot = await getDocs(existingReminderQuery);
      
      if (!existingReminderSnapshot.empty) {
        console.log('Practice reminder already sent today');
        return;
      }

      // Create practice reminder notification
      const reminderNotification = {
        id: `reminder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'practice_reminder',
        fromUserId: 'system',
        fromUserNickname: 'Practice Buddy',
        toUserId: currentUser.uid,
        message: `Your practice session is scheduled to start in 15 minutes at ${scheduledTime.toLocaleTimeString()}! Time to get ready! 🎵`,
        reason: 'upcoming_practice',
        scheduledTime: userData.todayPracticeSession.time,
        timestamp: serverTimestamp(),
        read: false
      };

      await addDoc(collection(db, 'notifications'), reminderNotification);

      // Send browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Practice Time Coming Up! 🎵', {
          body: `Your practice session starts in 15 minutes at ${scheduledTime.toLocaleTimeString()}`,
          icon: '/favicon.ico',
          tag: `practice-reminder-${currentUser.uid}`
        });
      }

      console.log('Practice reminder notification sent successfully');
      
    } catch (error) {
      console.error('Error checking upcoming practice reminders:', error);
    }
  };

  const checkMissedPracticeSessions = async () => {
    if (!currentUser || buddies.length === 0) return;

    try {
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      
      for (const buddy of buddies) {
        try {
          // Get buddy's user data to check their scheduled practice time
          const buddyDoc = await getDoc(doc(db, 'users', buddy.uid));
          if (!buddyDoc.exists()) continue;
          
          const buddyData = buddyDoc.data();
          const nextSession = buddyData.nextPracticeSession;
          
          if (!nextSession || nextSession.date !== today) continue;
          
          // Parse the scheduled time
          const scheduledTime = new Date(`${today}T${nextSession.time}`);
          const fifteenMinutesLater = new Date(scheduledTime.getTime() + 15 * 60 * 1000);
          
          // Check if it's past 15 minutes after scheduled time and before end of day
          if (now > fifteenMinutesLater && now < new Date(`${today}T23:59:59`)) {
            // Check if buddy has started any practice session today
            const sessionsQuery = query(
              collection(db, 'practiceSessions'),
              where('studentId', '==', buddy.uid),
              where('startAt', '>=', new Date(`${today}T00:00:00`)),
              where('startAt', '<=', new Date(`${today}T23:59:59`))
            );
            
            const sessionsSnapshot = await getDocs(sessionsQuery);
            
            // If no sessions found, create a nudge card
            if (sessionsSnapshot.empty) {
              // Check if we already created a nudge for this buddy today
              const existingNudgeQuery = query(
                collection(db, 'feedEvents'),
                where('type', '==', 'practice_reminder'),
                where('fromUserId', '==', currentUser.uid),
                where('toUserId', '==', buddy.uid),
                where('timestamp', '>=', new Date(`${today}T00:00:00`)),
                where('timestamp', '<=', new Date(`${today}T23:59:59`))
              );
              
              const existingNudgeSnapshot = await getDocs(existingNudgeQuery);
              
              if (existingNudgeSnapshot.empty) {
                // Create nudge feed event
                await addDoc(collection(db, 'feedEvents'), {
                  type: 'practice_reminder',
                  fromUserId: currentUser.uid,
                  fromUserNickname: currentUser.nickname || 'Unknown',
                  toUserId: buddy.uid,
                  toUserNickname: buddy.nickname || 'Unknown',
                  scheduledTime: nextSession.time,
                  timestamp: serverTimestamp(),
                  hearts: [],
                  nudges: []
                });
              }
            }
          }
        } catch (buddyError) {
          // Silently handle individual buddy errors
        }
      }
    } catch (error) {
      console.error('Error checking missed practice sessions:', error);
    }
  };

  // Send heart function
  const sendHeart = async (toUserId: string, eventId: string) => {
    if (!currentUser) return;
    
    try {
      // Find the event to add heart to
      const event = feedEvents.find(e => e.id === eventId);
      if (!event) return;

      // Check if user already sent a heart to this event
      const currentHearts = event.hearts || [];
      if (currentHearts.includes(currentUser.uid)) {
        console.log('User already sent a heart to this event');
        return;
      }

      // Update the event's hearts array in Firestore
      const eventRef = doc(db, 'feedEvents', eventId);
      const updatedHearts = [...currentHearts, currentUser.uid];
      
      await updateDoc(eventRef, {
        hearts: updatedHearts
      });

      // Track that we've sent a heart to this specific event for immediate UI feedback
      setSentHearts(prev => {
        const newSet = new Set(prev);
        newSet.add(eventId);
        return newSet;
      });
      
      console.log('Heart sent successfully');
    } catch (error) {
      console.error('Error sending heart:', error);
    }
  };

  // Send nudge function
  const sendNudge = async (toUserId: string, eventId: string) => {
    if (!currentUser) return;
    
    try {
      const buddy = buddies.find(b => b.uid === toUserId);
      const buddyName = buddy?.nickname || 'Unknown';
      
      // Track that we've sent a nudge to this specific event for immediate UI feedback
      setSentNudges(prev => {
        const newSet = new Set(prev);
        newSet.add(eventId);
        return newSet;
      });
      
      // Create a nudge notification for the target user
      const nudgeNotification = {
        id: `nudge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'nudge_received',
        fromUserId: currentUser.uid,
        fromUserNickname: currentUser.nickname || 'Unknown',
        toUserId: toUserId,
        message: `${currentUser.nickname || 'Someone'} sent you a nudge to help keep you on track with practice!`,
        reason: 'missed_practice',
        timestamp: serverTimestamp(),
        read: false
      };

      // Add notification to target user's notifications collection
      await addDoc(collection(db, 'notifications'), nudgeNotification);

      // Update the feed event to track that this user has sent a nudge
      const eventRef = doc(db, 'feedEvents', eventId);
      await updateDoc(eventRef, {
        nudges: arrayUnion(currentUser.uid)
      });

      // Update user's nudges received count
      const userRef = doc(db, 'users', toUserId);
      await updateDoc(userRef, {
        nudgesReceived: arrayUnion(currentUser.uid)
      });

      // No browser notification needed - button already shows "Nudge sent!" feedback

      console.log('Nudge sent successfully with notification');
    } catch (error) {
      console.error('Error sending nudge:', error);
      // Remove from sent nudges if there was an error
      setSentNudges(prev => {
        const newSet = new Set(prev);
        newSet.delete(eventId);
        return newSet;
      });
    }
  };

  // Check if a user has started practice today
  const hasUserStartedPracticeToday = async (userId: string): Promise<boolean> => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const startOfDay = new Date(`${today}T00:00:00`);
      const endOfDay = new Date(`${today}T23:59:59`);
      
      // Query only by studentId to avoid composite index requirement
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', userId)
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      // Filter client-side for today's sessions
      const todaySessions = sessionsSnapshot.docs.filter(doc => {
        const sessionData = doc.data();
        const sessionDate = sessionData.startAt?.toDate?.() || new Date(sessionData.startAt);
        return sessionDate >= startOfDay && sessionDate <= endOfDay;
      });
      
      return todaySessions.length > 0;
    } catch (error) {
      console.error('Error checking practice status:', error);
      return false;
    }
  };

  // Check practice status for all users with practice_reminder events
  const checkPracticeStatusForReminders = async () => {
    if (!feedEvents.length) return;
    
    const reminderEvents = feedEvents.filter(event => event.type === 'practice_reminder');
    if (reminderEvents.length === 0) return;
    
    const userIds = reminderEvents.map(event => event.toUserId!).filter(Boolean);
    const practiceStatuses = await Promise.all(
      userIds.map(async (userId) => ({
        userId,
        hasStarted: await hasUserStartedPracticeToday(userId)
      }))
    );
    
    const startedUsers = new Set(
      practiceStatuses
        .filter(status => status.hasStarted)
        .map(status => status.userId)
    );
    
    setUsersStartedPractice(startedUsers);
  };

  // Format timestamp
  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  // Fetch latest lesson and extract key quotes
  const fetchLatestLessonQuotes = async (studentId: string) => {
    try {
      const lessonsQuery = query(
        collection(db, 'lessons'),
        where('studentId', '==', studentId),
        where('status', '==', 'completed')
      );
      const lessonsSnapshot = await getDocs(lessonsQuery);
      
      if (!lessonsSnapshot.empty) {
        // Sort by createdAt to get the most recent lesson
        const lessons = lessonsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as any))
          .sort((a, b) => {
            const aTime = a.createdAt?.toDate?.() || new Date(0);
            const bTime = b.createdAt?.toDate?.() || new Date(0);
            return bTime.getTime() - aTime.getTime();
          });
        
        const latestLesson = lessons[0];
        if (latestLesson.keyQuotes && Array.isArray(latestLesson.keyQuotes)) {
          setLatestLessonQuotes(latestLesson.keyQuotes);
        }
      }
    } catch (error) {
      console.error('Error fetching latest lesson quotes:', error);
    }
  };

  // Check if student is linked to any teachers by searching teacher's linkedStudents arrays
  const checkTeacherLinking = async (studentId: string) => {
    try {
      // First, get all teachers (this should work with the current rules)
      const teachersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'teacher')
      );
      const teachersSnapshot = await getDocs(teachersQuery);
      
      const linkedTeacherIds = [];
      
      // Check each teacher individually to see if they have this student
      for (const teacherDoc of teachersSnapshot.docs) {
        try {
          const teacherData = teacherDoc.data();
          
          if (teacherData.linkedStudents && teacherData.linkedStudents.includes(studentId)) {
            linkedTeacherIds.push(teacherDoc.id);
          }
        } catch (teacherError) {
          console.warn(`Error checking teacher ${teacherDoc.id}:`, teacherError);
        }
      }
      
      if (linkedTeacherIds.length > 0) {
        // Student is linked to at least one teacher, update their linkedTeachers array
        const studentRef = doc(db, 'users', studentId);
        await updateDoc(studentRef, {
          linkedTeachers: linkedTeacherIds
        });
        
        // Fetch the first teacher's name for display
        if (linkedTeacherIds.length > 0) {
          try {
            const firstTeacherDoc = await getDoc(doc(db, 'users', linkedTeacherIds[0]));
            if (firstTeacherDoc.exists()) {
              const teacherData = firstTeacherDoc.data();
              setLinkedTeacherName(teacherData.nickname || teacherData.name || 'Teacher');
            }
          } catch (error) {
            console.warn('Error fetching teacher name:', error);
            setLinkedTeacherName('Teacher');
          }
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking teacher linking:', error);
      return false;
    }
  };

  // Refresh user data from Firestore to get latest avatar and linking status
  const refreshUserData = async () => {
    if (!currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const updatedUser = {
          uid: currentUser.uid,
          ...userData
        } as User;
        
        // If this is a student and they don't have linkedTeachers but might be linked
        if (updatedUser.role === 'student' && (!updatedUser.linkedTeachers || updatedUser.linkedTeachers.length === 0)) {
          const isLinked = await checkTeacherLinking(currentUser.uid);
          if (isLinked) {
            // Refresh the user data again to get the updated linkedTeachers
            const refreshedUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (refreshedUserDoc.exists()) {
              const refreshedUserData = refreshedUserDoc.data();
              updatedUser.linkedTeachers = refreshedUserData.linkedTeachers || [];
            }
          }
        }
        
        // If student already has linkedTeachers, fetch the teacher name
        if (updatedUser.role === 'student' && updatedUser.linkedTeachers && updatedUser.linkedTeachers.length > 0) {
          try {
            const firstTeacherDoc = await getDoc(doc(db, 'users', updatedUser.linkedTeachers[0]));
            if (firstTeacherDoc.exists()) {
              const teacherData = firstTeacherDoc.data();
              setLinkedTeacherName(teacherData.nickname || teacherData.name || 'Teacher');
            }
          } catch (error) {
            console.warn('Error fetching existing teacher name:', error);
            setLinkedTeacherName('Teacher');
          }
        }
        
        setRefreshedUser(updatedUser);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Badge definitions
  const badgeDefinitions: Record<string, { emoji: string; name: string; description: string; color: string }> = {
    'first-day': { emoji: '🎯', name: 'First Steps', description: 'Completed your first practice session', color: 'from-blue-500 to-cyan-500' },
    'three-day-streak': { emoji: '🔥', name: 'Hot Streak', description: '3-day practice streak', color: 'from-orange-500 to-red-500' },
    'week-streak': { emoji: '⭐', name: 'Week Warrior', description: '7-day practice streak', color: 'from-yellow-500 to-orange-500' },
    'two-week-streak': { emoji: '💪', name: 'Consistency King', description: '14-day practice streak', color: 'from-green-500 to-emerald-500' },
    'month-streak': { emoji: '👑', name: 'Practice Legend', description: '30-day practice streak', color: 'from-purple-500 to-pink-500' },
    'first-week': { emoji: '📅', name: 'Week Complete', description: 'Met weekly goal once', color: 'from-indigo-500 to-blue-500' },
    'month-consistency': { emoji: '🏆', name: 'Monthly Master', description: '4 weeks of meeting goals', color: 'from-amber-500 to-yellow-500' },
    'quarter-master': { emoji: '🎖️', name: 'Quarter Champion', description: '12 weeks of meeting goals', color: 'from-rose-500 to-pink-500' },
    'hour-master': { emoji: '⏰', name: 'Hour Hero', description: '1 hour total practice time', color: 'from-teal-500 to-cyan-500' },
    'five-hour-club': { emoji: '🎵', name: 'Five Hour Club', description: '5 hours total practice time', color: 'from-violet-500 to-purple-500' },
    'thousand-minute-master': { emoji: '💯', name: 'Thousand Minute Master', description: '1000 minutes total practice', color: 'from-emerald-500 to-green-500' },
    'practice-legend': { emoji: '🌟', name: 'Practice Legend', description: '5000 minutes total practice', color: 'from-yellow-400 to-orange-500' },
    'focused-practicer': { emoji: '🎯', name: 'Focused Practicer', description: 'Average 30+ min sessions', color: 'from-blue-600 to-indigo-600' },
    'dedicated-musician': { emoji: '🎼', name: 'Dedicated Musician', description: 'Average 60+ min sessions', color: 'from-purple-600 to-violet-600' },
    
    // Teacher Assigned Badges
    'performance-ready': { emoji: '🎭', name: 'Performance Ready', description: 'Teacher says you\'re ready to perform!', color: 'from-rose-500 to-pink-600' },
    'breakthrough-moment': { emoji: '💡', name: 'Breakthrough Moment', description: 'Teacher recognised you had a major breakthrough', color: 'from-yellow-400 to-orange-500' },
    'technique-perfectionist': { emoji: '✨', name: 'Technique Perfectionist', description: 'Teacher praised your technique work', color: 'from-blue-400 to-indigo-500' },
    'musical-expression': { emoji: '🎨', name: 'Musical Expression', description: 'Teacher loved your musical interpretation', color: 'from-purple-400 to-pink-500' },
    'sight-reading-star': { emoji: '👁️', name: 'Sight Reading Star', description: 'Teacher impressed by your sight reading', color: 'from-green-400 to-teal-500' },
    'memory-master': { emoji: '🧠', name: 'Memory Master', description: 'Teacher praised your memorisation skills', color: 'from-violet-400 to-purple-500' },
    'rhythm-king': { emoji: '🥁', name: 'Rhythm King/Queen', description: 'Teacher loved your rhythm work', color: 'from-red-400 to-pink-500' },
    'practice-champion': { emoji: '🏅', name: 'Practice Champion', description: 'Teacher noticed your excellent practice habits', color: 'from-amber-400 to-yellow-500' },
    'creative-genius': { emoji: '🎪', name: 'Creative Genius', description: 'Teacher praised your creativity', color: 'from-pink-400 to-rose-500' },
    'dedication-award': { emoji: '💎', name: 'Dedication Award', description: 'Teacher recognised your dedication', color: 'from-indigo-400 to-blue-500' },
    'improvement-star': { emoji: '⭐', name: 'Improvement Star', description: 'Teacher noticed significant improvement', color: 'from-emerald-400 to-green-500' },
    'lesson-hero': { emoji: '🦸', name: 'Lesson Hero', description: 'Teacher said you had an amazing lesson', color: 'from-orange-400 to-red-500' },

    // String Technique Badges - Bow Technique
    'bow-hold-beginner': { emoji: '🤏', name: 'Bow Hold Beginner', description: 'You are learning a correct bow hold', color: 'from-amber-400 to-yellow-500' },
    'bow-hold-developing': { emoji: '✊', name: 'Bow Hold Developing', description: 'You now have a stable bow hold', color: 'from-yellow-400 to-orange-500' },
    'bow-hold-master': { emoji: '👑', name: 'Bow Hold Master', description: 'You consistently play with an excellent bow hold', color: 'from-orange-400 to-red-500' },
    
    'straight-bow-beginner': { emoji: '📏', name: 'Straight Bow Beginner', description: 'You are learning to keep the bow straight', color: 'from-blue-400 to-indigo-500' },
    'straight-bow-developing': { emoji: '📐', name: 'Straight Bow Developing', description: 'You now understand how to keep the bow straight', color: 'from-indigo-400 to-purple-500' },
    'straight-bow-champion': { emoji: '🏆', name: 'Straight Bow Champion', description: 'You consistently play with a straight bow', color: 'from-purple-400 to-pink-500' },
    
    'spiccato-beginner': { emoji: '⚡', name: 'Spiccato Beginner', description: 'You are beginning to learn spiccato', color: 'from-green-400 to-teal-500' },
    'spiccato-developing': { emoji: '💫', name: 'Spiccato Developing', description: 'You now understand how to bounce the bow clearly', color: 'from-teal-400 to-cyan-500' },
    'spiccato-specialist': { emoji: '⭐', name: 'Spiccato Specialist', description: 'You consistently play controlled spiccato', color: 'from-cyan-400 to-blue-500' },
    
    'staccato-beginner': { emoji: '🔸', name: 'Staccato Beginner', description: 'You are learning short, separated strokes', color: 'from-red-400 to-pink-500' },
    'staccato-developing': { emoji: '🔹', name: 'Staccato Developing', description: 'You now understand how to play staccato cleanly', color: 'from-pink-400 to-rose-500' },
    'staccato-star': { emoji: '✨', name: 'Staccato Star', description: 'You consistently play crisp staccato', color: 'from-rose-400 to-red-500' },
    
    'legato-beginner': { emoji: '🌊', name: 'Legato Beginner', description: 'You are learning smooth connected bowing', color: 'from-violet-400 to-purple-500' },
    'legato-developing': { emoji: '🎵', name: 'Legato Developing', description: 'You now have flowing legato phrases', color: 'from-purple-400 to-indigo-500' },
    'legato-legend': { emoji: '🎼', name: 'Legato Legend', description: 'You consistently play beautiful legato', color: 'from-indigo-400 to-blue-500' },
    
    'bow-distribution-beginner': { emoji: '⚖️', name: 'Bow Distribution Beginner', description: 'You are learning how to share bow evenly', color: 'from-emerald-400 to-green-500' },
    'bow-distribution-developing': { emoji: '🎯', name: 'Bow Distribution Developing', description: 'You now understand bow planning', color: 'from-green-400 to-emerald-500' },
    'bow-distribution-pro': { emoji: '🏅', name: 'Bow Distribution Pro', description: 'You consistently use bow distribution effectively', color: 'from-emerald-400 to-teal-500' },
    
    'sautille-beginner': { emoji: '🦗', name: 'Sautillé Beginner', description: 'You are exploring Sautillé strokes', color: 'from-lime-400 to-green-500' },
    'sautille-developing': { emoji: '🦎', name: 'Sautillé Developing', description: 'You now understand how to play Sautillé', color: 'from-green-400 to-emerald-500' },
    'sautille-master': { emoji: '🦅', name: 'Sautillé Master', description: 'You consistently play controlled Sautillé', color: 'from-emerald-400 to-teal-500' },

    // String Technique Badges - Left Hand Technique
    'vibrato-beginner': { emoji: '🌊', name: 'Vibrato Beginner', description: 'You are starting to explore vibrato', color: 'from-pink-400 to-rose-500' },
    'vibrato-developing': { emoji: '💫', name: 'Vibrato Developing', description: 'You now have basic vibrato control', color: 'from-rose-400 to-red-500' },
    'vibrato-virtuoso': { emoji: '🎭', name: 'Vibrato Virtuoso', description: 'You consistently play with beautiful vibrato', color: 'from-red-400 to-pink-500' },
    
    'intonation-beginner': { emoji: '🎯', name: 'Intonation Beginner', description: 'You are learning to check finger accuracy', color: 'from-blue-400 to-indigo-500' },
    'intonation-developing': { emoji: '📐', name: 'Intonation Developing', description: 'You now understand how to adjust intonation', color: 'from-indigo-400 to-purple-500' },
    'intonation-ace': { emoji: '🎪', name: 'Intonation Ace', description: 'You consistently play with accurate intonation', color: 'from-purple-400 to-pink-500' },
    
    'shifting-beginner': { emoji: '🚀', name: 'Shifting Beginner', description: 'You are beginning to shift between positions', color: 'from-orange-400 to-red-500' },
    'shifting-developing': { emoji: '🎢', name: 'Shifting Developing', description: 'You now have smoother position changes', color: 'from-red-400 to-pink-500' },
    'shifting-star': { emoji: '⭐', name: 'Shifting Star', description: 'You consistently shift with accuracy and ease', color: 'from-pink-400 to-rose-500' },
    
    'double-stops-beginner': { emoji: '🤝', name: 'Double Stops Beginner', description: 'You are learning to balance two notes', color: 'from-violet-400 to-purple-500' },
    'double-stops-developing': { emoji: '⚖️', name: 'Double Stops Developing', description: 'You now understand how to tune double stops', color: 'from-purple-400 to-indigo-500' },
    'double-stops-master': { emoji: '🎼', name: 'Double Stops Master', description: 'You consistently play clear, balanced double stops', color: 'from-indigo-400 to-blue-500' },
    
    'trills-beginner': { emoji: '🦋', name: 'Trills Beginner', description: 'You are learning how to play trills', color: 'from-emerald-400 to-green-500' },
    'trills-developing': { emoji: '🌿', name: 'Trills Developing', description: 'You now understand how to make trills even', color: 'from-green-400 to-emerald-500' },
    'trills-talent': { emoji: '🎪', name: 'Trills Talent', description: 'You consistently play fast, clear trills', color: 'from-emerald-400 to-teal-500' },

    // String Technique Badges - Advanced Techniques
    'harmonics-beginner': { emoji: '🔮', name: 'Harmonics Beginner', description: 'You are discovering natural harmonics', color: 'from-cyan-400 to-blue-500' },
    'harmonics-developing': { emoji: '✨', name: 'Harmonics Developing', description: 'You now understand how to produce harmonics', color: 'from-blue-400 to-indigo-500' },
    'harmonics-hero': { emoji: '🦸', name: 'Harmonics Hero', description: 'You consistently play clear harmonics', color: 'from-indigo-400 to-purple-500' },
    
    'pizzicato-beginner': { emoji: '👆', name: 'Pizzicato Beginner', description: 'You are learning how to pluck clearly', color: 'from-amber-400 to-yellow-500' },
    'pizzicato-developing': { emoji: '🤏', name: 'Pizzicato Developing', description: 'You now understand how to project pizzicato', color: 'from-yellow-400 to-orange-500' },
    'pizzicato-pro': { emoji: '💪', name: 'Pizzicato Pro', description: 'You consistently play strong, even pizzicato', color: 'from-orange-400 to-red-500' },
    
    'col-legno-beginner': { emoji: '🪵', name: 'Col Legno Beginner', description: 'You are exploring col legno strokes', color: 'from-stone-400 to-gray-500' },
    'col-legno-developing': { emoji: '🌳', name: 'Col Legno Developing', description: 'You now understand how to play col legno', color: 'from-gray-400 to-zinc-500' },
    'col-legno-champion': { emoji: '🏆', name: 'Col Legno Champion', description: 'You consistently use col legno effectively', color: 'from-zinc-400 to-neutral-500' },
    
    'sul-ponticello-beginner': { emoji: '🌊', name: 'Sul Ponticello Beginner', description: 'You are exploring sul ponticello sounds', color: 'from-sky-400 to-blue-500' },
    'sul-ponticello-developing': { emoji: '🌀', name: 'Sul Ponticello Developing', description: 'You now understand how to control sul ponticello', color: 'from-blue-400 to-indigo-500' },
    'sul-ponticello-star': { emoji: '💫', name: 'Sul Ponticello Star', description: 'You consistently create a strong sul ponticello sound', color: 'from-indigo-400 to-purple-500' },

    // Universal Technique Badges - Musical Expression
    'dynamics-beginner': { emoji: '🔊', name: 'Dynamics Beginner', description: 'You are learning to play soft and loud with control', color: 'from-blue-400 to-indigo-500' },
    'dynamics-developing': { emoji: '🎚️', name: 'Dynamics Developing', description: 'You now understand how to shape dynamics effectively', color: 'from-indigo-400 to-purple-500' },
    'dynamics-expert': { emoji: '🎛️', name: 'Dynamics Expert', description: 'You consistently control dynamics with expression', color: 'from-purple-400 to-pink-500' },
    
    'phrasing-beginner': { emoji: '🎵', name: 'Phrasing Beginner', description: 'You are learning to shape musical phrases', color: 'from-green-400 to-emerald-500' },
    'phrasing-developing': { emoji: '🌊', name: 'Phrasing Developing', description: 'You now understand how to connect notes into flowing phrases', color: 'from-emerald-400 to-teal-500' },
    'phrasing-pro': { emoji: '🎼', name: 'Phrasing Pro', description: 'You consistently play with expressive phrasing', color: 'from-teal-400 to-cyan-500' },
    
    'articulation-beginner': { emoji: '🎯', name: 'Articulation Beginner', description: 'You are learning clear starts and stops', color: 'from-orange-400 to-red-500' },
    'articulation-developing': { emoji: '🎪', name: 'Articulation Developing', description: 'You now understand how to vary articulation styles', color: 'from-red-400 to-pink-500' },
    'articulation-ace': { emoji: '🏆', name: 'Articulation Ace', description: 'You consistently play with clear, accurate articulation', color: 'from-pink-400 to-rose-500' },
    
    'tone-quality-beginner': { emoji: '🌟', name: 'Tone Quality Beginner', description: 'You are learning to produce a steady tone', color: 'from-yellow-400 to-orange-500' },
    'tone-quality-developing': { emoji: '✨', name: 'Tone Quality Developing', description: 'You now understand how to refine and shape your tone', color: 'from-orange-400 to-red-500' },
    'tone-quality-star': { emoji: '💫', name: 'Tone Quality Star', description: 'You consistently create a beautiful, resonant tone', color: 'from-red-400 to-pink-500' },

    // Universal Technique Badges - Technical Foundation
    'posture-beginner': { emoji: '🧍', name: 'Posture Beginner', description: 'You are learning correct playing posture', color: 'from-emerald-400 to-green-500' },
    'posture-developing': { emoji: '🚶', name: 'Posture Developing', description: 'You now understand how posture affects your playing', color: 'from-green-400 to-emerald-500' },
    'posture-perfect': { emoji: '👑', name: 'Posture Perfect', description: 'You consistently maintain excellent posture', color: 'from-emerald-400 to-teal-500' },
    
    'relaxation-beginner': { emoji: '🌱', name: 'Relaxation Beginner', description: 'You are learning to release tension while playing', color: 'from-lime-400 to-green-500' },
    'relaxation-developing': { emoji: '🌿', name: 'Relaxation Developing', description: 'You now understand how to stay relaxed under pressure', color: 'from-green-400 to-emerald-500' },
    'relaxation-mastery': { emoji: '🕊️', name: 'Relaxation Mastery', description: 'You consistently play with freedom and no excess tension', color: 'from-emerald-400 to-teal-500' },
    
    'endurance-beginner': { emoji: '⏱️', name: 'Endurance Beginner', description: 'You are learning to maintain focus in practice', color: 'from-violet-400 to-purple-500' },
    'endurance-developing': { emoji: '💪', name: 'Endurance Developing', description: 'You now understand how to build stamina in longer sessions', color: 'from-purple-400 to-indigo-500' },
    'endurance-champion': { emoji: '🏅', name: 'Endurance Champion', description: 'You consistently sustain energy and concentration in practice', color: 'from-indigo-400 to-blue-500' },

    // Special Occasion Badges
    'birthday-celebration': { emoji: '🎂', name: 'Birthday Practice Star', description: 'You practised on your birthday! What dedication!', color: 'from-pink-400 to-purple-600' }
  };


  // Check for birthday and show celebration
  const checkBirthdayAndShowCelebration = () => {
    if (currentUser?.birthdate && isTodayBirthday(currentUser.birthdate)) {
      // Check if we've already shown the birthday popup today
      const lastBirthdayShown = localStorage.getItem(`birthdayShown_${currentUser.uid}_${new Date().toDateString()}`);
      if (!lastBirthdayShown) {
        setShowBirthdayCelebration(true);
        // Mark as shown for today
        localStorage.setItem(`birthdayShown_${currentUser.uid}_${new Date().toDateString()}`, 'true');
      }
    }
  };

  // Check if weekly goal was reached and create feed event
  const checkWeeklyGoalReached = async (weeklyCompletedSessionsCount: number) => {
    if (!currentUser) return;
    
    const weeklyGoal = currentUser.practiceSessionsPerWeek || 3;
    
    // Check if weekly goal was reached
    if (weeklyCompletedSessionsCount >= weeklyGoal) {
      try {
        // Check if we already created a weekly goal feed event this week
        const now = new Date();
        const currentDay = now.getDay();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - currentDay);
        const weekKey = startOfWeek.toISOString().split('T')[0];
        const lastWeeklyGoalWeek = localStorage.getItem(`lastWeeklyGoalFeed_${currentUser.uid}`);
        
        if (lastWeeklyGoalWeek !== weekKey) {
          // Create weekly goal reached feed event
          await createFeedEvent({
            type: 'weekly_goal_reached',
            fromUserId: currentUser.uid,
            fromUserNickname: currentUser.nickname || currentUser.name || 'Unknown'
          });
          
          // Store this week's key to prevent duplicate events
          localStorage.setItem(`lastWeeklyGoalFeed_${currentUser.uid}`, weekKey);
        }
      } catch (error) {
        console.error('Error creating weekly goal feed event:', error);
      }
    }
  };

  // Create a feed event
  const createFeedEvent = async (eventData: Omit<FeedEvent, 'id' | 'timestamp' | 'hearts' | 'nudges'>) => {
    try {
      const eventRef = await addDoc(collection(db, 'feedEvents'), {
        ...eventData,
        timestamp: serverTimestamp(),
        hearts: [],
        nudges: []
      });
      return eventRef.id;
    } catch (error) {
      console.error('Error creating feed event:', error);
    }
  };

  // Load badges
  const loadBadges = async () => {
    if (!currentUser) return;
    
    try {
      const allBadges = await loadAllUserBadges(currentUser.uid, db);
      
      // Check for new badges by comparing with previously stored badges
      const storedBadges = localStorage.getItem(`badges_${currentUser.uid}`);
      const previousBadges = storedBadges ? JSON.parse(storedBadges) : [];
      
      // Find new badges
      const newBadges = allBadges.filter((badgeId: string) => !previousBadges.includes(badgeId));
      
      // Show badge celebration for new badges
      if (newBadges.length > 0) {
        
        // Show celebration for the first new badge
        const firstNewBadge = newBadges[0];
        const badgeDef = badgeDefinitions[firstNewBadge];
        if (badgeDef) {
          setNewBadge({
            id: firstNewBadge,
            name: badgeDef.name,
            description: badgeDef.description,
            emoji: badgeDef.emoji,
            color: badgeDef.color
          });
          setShowBadgeCelebration(true);
          
          // Play celebration sound
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT');
            audio.play().catch(() => {}); // Ignore errors if audio fails
          } catch (error) {
            // Ignore audio errors
          }
        }
      }
      
      // Create feed events for new badges
      if (newBadges.length > 0) {
        for (const badgeId of newBadges) {
          // Get badge name from badge definitions
          const badgeDefinitions = {
            'first-day': { name: 'First Day' },
            'week-warrior': { name: 'Week Warrior' },
            'month-master': { name: 'Month Master' },
            'streak-starter': { name: 'Streak Starter' },
            'streak-sustainer': { name: 'Streak Sustainer' },
            'streak-champion': { name: 'Streak Champion' },
            'time-titan': { name: 'Time Titan' },
            'marathon-musician': { name: 'Marathon Musician' },
            'early-bird': { name: 'Early Bird' },
            'night-owl': { name: 'Night Owl' },
            'weekend-warrior': { name: 'Weekend Warrior' },
            'comeback-kid': { name: 'Comeback Kid' },
            'birthday-celebration': { name: 'Birthday Celebration' },
            'technique-master': { name: 'Technique Master' },
            'piece-perfectionist': { name: 'Piece Perfectionist' },
            'category-explorer': { name: 'Category Explorer' }
          };
          
          const badgeName = badgeDefinitions[badgeId as keyof typeof badgeDefinitions]?.name || badgeId;
          
          await createFeedEvent({
            type: 'badge_earned',
            fromUserId: currentUser.uid,
            fromUserNickname: currentUser.nickname || currentUser.name || 'Unknown',
            badgeName: badgeName
          });
        }
        
        // Update stored badges
        localStorage.setItem(`badges_${currentUser.uid}`, JSON.stringify(allBadges));
      }
      
      setEarnedBadges(allBadges);
      setBadgesLoaded(true);
      
      // Check if they have the birthday badge
      setHasBirthdayBadge(allBadges.includes('birthday-celebration'));
    } catch (error) {
      console.error('Error loading badges:', error);
      setBadgesLoaded(true);
    }
  };

  // Clean up old incomplete practice sessions
  const cleanupOldIncompleteSessions = async () => {
    if (!currentUser) return;
    
    try {
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', currentUser.uid),
        where('completed', '==', false)
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      if (!sessionsSnapshot.empty) {
        
        const now = new Date();
        const cleanupPromises = [];
        
        for (const sessionDoc of sessionsSnapshot.docs) {
          const sessionData = sessionDoc.data();
          const startTime = sessionData.startAt?.toDate?.() || new Date(sessionData.startAt);
          const hoursSinceStart = (now.getTime() - startTime.getTime()) / (1000 * 60 * 60);
          
          // Delete sessions older than 2 hours
          if (hoursSinceStart > 2) {
            cleanupPromises.push(deleteDoc(sessionDoc.ref));
          }
        }
        
        if (cleanupPromises.length > 0) {
          await Promise.all(cleanupPromises);
        }
      }
    } catch (error) {
      console.error('Error cleaning up old sessions:', error);
    }
  };

  // Check for active practice sessions and update progress
  const checkActivePracticeSession = async () => {
    if (!currentUser) return;
    
    try {
      // Check for active practice sessions first
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', currentUser.uid),
        where('completed', '==', false)
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      if (!sessionsSnapshot.empty) {
        
        // Find the most recent active session
        const activeSession = sessionsSnapshot.docs[0].data();
        const plannedMinutes = activeSession.plannedTotalMinutes || currentUser.practiceSessionDuration || 30;
        const startTime = activeSession.startAt?.toDate?.() || new Date(activeSession.startAt);
        const now = new Date();
        const elapsedSeconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
        const totalSeconds = plannedMinutes * 60;
        
        // Only consider sessions that started within the last 2 hours as "active"
        // This prevents old incomplete sessions from showing as live
        const hoursSinceStart = elapsedSeconds / 3600;
        
        if (hoursSinceStart > 2) {
          // Clean up old sessions only when we find them
          await cleanupOldIncompleteSessions();
          
          // Force state update with a small delay to ensure it takes effect
          setTimeout(() => {
            setIsPracticeSessionActive(false);
            setCurrentSessionProgress(0);
          }, 100);
          return;
        }
        
        // Calculate progress percentage (0-100)
        const progress = Math.min(100, Math.max(0, (elapsedSeconds / totalSeconds) * 100));
        
        // Setting practice session as active
        setIsPracticeSessionActive(true);
        setCurrentSessionProgress(progress);
      } else {
        // Force state update
        setTimeout(() => {
          setIsPracticeSessionActive(false);
          setCurrentSessionProgress(0);
        }, 100);
      }
    } catch (error) {
      console.error('Error checking active practice session:', error);
      // Force state update on error
      setTimeout(() => {
        setIsPracticeSessionActive(false);
        setCurrentSessionProgress(0);
      }, 100);
    }
  };

  // Load weekly progress
  const loadWeeklyProgress = async () => {
    if (!currentUser?.practiceSessionsPerWeek) return;
    
    try {
      // Calculate the start of the current week (Sunday)
      const now = new Date();
      const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - currentDay); // Go back to Sunday
      startOfWeek.setHours(0, 0, 0, 0); // Start of day
      
      // Calculate end of week (Saturday)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999); // End of day
      
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', currentUser.uid),
        where('completed', '==', true),
        where('endAt', '>=', startOfWeek),
        where('endAt', '<=', endOfWeek),
        orderBy('endAt', 'desc')
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessions: any[] = [];
      
      sessionsSnapshot.forEach((doc) => {
        const data = doc.data();
        sessions.push({
          id: doc.id,
          endAt: data.endAt,
          durationSec: data.durationSec
        });
      });
      
      // Store all sessions for total weekly time calculation
      setAllWeeklySessions(sessions);
      
      // Calculate goal-reached days based on cumulative daily practice time
      const goalMinutes = currentUser?.practiceSessionDuration || 30;
      
      // Group sessions by day
      const sessionsByDay: { [dateKey: string]: any[] } = {};
      sessions.forEach(session => {
        const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
        const dateKey = sessionDate.toDateString(); // e.g., "Mon Oct 21 2024"
        
        if (!sessionsByDay[dateKey]) {
          sessionsByDay[dateKey] = [];
        }
        sessionsByDay[dateKey].push(session);
      });
      
      // Check each day to see if cumulative practice meets the daily goal
      const goalReachedDays: any[] = [];
      Object.values(sessionsByDay).forEach(daySessions => {
        const dayTotalMinutes = daySessions.reduce((total, session) => {
          return total + (session.durationSec || 0) / 60;
        }, 0);
        
        // If daily total meets goal, add only ONE representative session per day
        // This ensures each calendar day counts as only one weekly goal achievement
        if (dayTotalMinutes >= goalMinutes) {
          // Use the first session of the day as the representative
          goalReachedDays.push(daySessions[0]);
        }
      });
      
      setWeeklyCompletedSessions(goalReachedDays);
      
      // Check if weekly goal was reached and create feed event
      await checkWeeklyGoalReached(goalReachedDays.length);
      
      setWeeklyProgressLoaded(true);
    } catch (error) {
      console.error('Error loading weekly progress:', error);
      setWeeklyProgressLoaded(true);
    }
  };

  // Render badge function
  const renderBadge = (badgeId: string) => {
    const badge = badgeDefinitions[badgeId];
    if (!badge) return null;

    return (
      <div
        key={badgeId}
        className={`bg-gradient-to-br ${badge.color} text-white p-2 rounded-full shadow-lg transform hover:scale-110 transition-all duration-300 cursor-pointer group relative`}
        title={`${badge.name}: ${badge.description}`}
      >
        <div className="text-lg sm:text-xl">{badge.emoji}</div>
        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
          <div className="font-semibold">{badge.name}</div>
          <div className="text-gray-300">{badge.description}</div>
        </div>
      </div>
    );
  };

  // Redirect teachers to teacher dashboard
  useEffect(() => {
    if (currentUser?.role === 'teacher') {
      navigate('/teacher/dashboard');
    }
  }, [currentUser, navigate]);

  // Load user data on component mount
  useEffect(() => {
    if (currentUser) {
      // Fetch latest lesson quotes for students
      if (currentUser.role === 'student') {
        fetchLatestLessonQuotes(currentUser.uid);
        loadBadges();
        loadWeeklyProgress();
        checkActivePracticeSession(); // Check for active practice sessions
        loadBuddies(); // Load buddies for feed
        // Check for birthday celebration after badges are loaded
        setTimeout(() => {
          checkBirthdayAndShowCelebration();
        }, 1000);
      }
      setLoading(false);
    }
  }, [currentUser]);

  // Set up polling for active practice session progress
  useEffect(() => {
    if (currentUser?.role === 'student') {
      // Check immediately
      checkActivePracticeSession();
      
      // Set up interval to check every 30 seconds when on dashboard
      const interval = setInterval(checkActivePracticeSession, 30000);
      
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Refresh weekly progress when user's practice frequency goal changes
  useEffect(() => {
    if (currentUser?.role === 'student' && currentUser.practiceSessionsPerWeek) {
      loadWeeklyProgress();
    }
  }, [currentUser?.practiceSessionsPerWeek]);

  // Refresh data when practice session duration changes
  useEffect(() => {
    if (currentUser?.role === 'student') {
      // Force a re-render to update the Daily Practice Goal card
      setLoading(false); // This will trigger a re-render with updated data
    }
  }, [currentUser?.practiceSessionDuration]);


  // Refresh data when component regains focus (e.g., returning from profile edit)
  useEffect(() => {
    const handleFocus = () => {
      if (currentUser?.role === 'student') {
        refreshUserData();
        loadWeeklyProgress();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [currentUser]);

  const handlePracticePlannerClick = () => {
    // Check for birthday celebration when clicking Practice Planner
    if (currentUser?.birthdate && isTodayBirthday(currentUser.birthdate)) {
      // Check if we've already shown the birthday popup today
      const lastBirthdayShown = localStorage.getItem(`birthdayShown_${currentUser.uid}_${new Date().toDateString()}`);
      
      if (!lastBirthdayShown) {
        setShowBirthdayCelebration(true);
        // Mark as shown for today
        localStorage.setItem(`birthdayShown_${currentUser.uid}_${new Date().toDateString()}`, 'true');
        // Don't navigate immediately - let the user see the birthday popup first
        return;
      }
    }
    navigate('/practice-tasks');
  };

  const handleBirthdayCelebrationClose = () => {
    setShowBirthdayCelebration(false);
    // Navigate to practice tasks after closing birthday popup
    navigate('/practice-tasks');
  };

  // Rotate quotes every 4 seconds
  useEffect(() => {
    if (latestLessonQuotes.length > 1) {
      const interval = setInterval(() => {
        setCurrentQuoteIndex((prevIndex) => 
          (prevIndex + 1) % latestLessonQuotes.length
        );
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [latestLessonQuotes]);

  // Load feed events when buddies are loaded or when component mounts
  useEffect(() => {
    loadFeedEvents();
  }, [buddies, currentUser]);

  // Check for missed practice sessions and upcoming reminders every 5 minutes
  useEffect(() => {
    if (currentUser?.role === 'student') {
      // Check immediately
      checkMissedPracticeSessions();
      checkUpcomingPracticeReminders();
      
      // Then check every 5 minutes
      const interval = setInterval(() => {
        checkMissedPracticeSessions();
        checkUpcomingPracticeReminders();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [currentUser, buddies]);

  // Check practice status when feed events change
  useEffect(() => {
    checkPracticeStatusForReminders();
  }, [feedEvents]);

  // Carousel functionality
  const startCarousel = () => {
    if (carouselInterval) {
      clearInterval(carouselInterval);
    }
    
    const interval = setInterval(() => {
      setCurrentCarouselIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % Math.min(feedEvents.length, 10);
        return nextIndex;
      });
    }, 4000); // Change card every 4 seconds
    
    setCarouselInterval(interval);
  };

  const stopCarousel = () => {
    if (carouselInterval) {
      clearInterval(carouselInterval);
      setCarouselInterval(null);
    }
  };

  const goToNextCard = () => {
    setCurrentCarouselIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % Math.min(feedEvents.length, 10);
      return nextIndex;
    });
  };

  const goToPreviousCard = () => {
    setCurrentCarouselIndex((prevIndex) => {
      const prevIndex_ = prevIndex - 1;
      return prevIndex_ < 0 ? Math.min(feedEvents.length, 10) - 1 : prevIndex_;
    });
  };

  // Start carousel when feed events are loaded
  useEffect(() => {
    if (feedEvents.length > 1) {
      startCarousel();
    }
    
    return () => {
      stopCarousel();
    };
  }, [feedEvents]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (carouselInterval) {
        clearInterval(carouselInterval);
      }
    };
  }, [carouselInterval]);

  if (!currentUser || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (currentUser.role === 'teacher') {
    return <div>Redirecting to teacher dashboard...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between h-12 sm:h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-xl sm:text-3xl mr-2 sm:mr-3">🎵</div>
                <h1 className="text-sm sm:text-xl font-bold text-white">
                Mindful Practice Buddy
              </h1>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="text-xs sm:text-sm text-white/90 font-medium hidden sm:block">
                Welcome, {refreshedUser?.nickname}
              </span>
              
              {/* Notification System */}
              <div className="text-white">
                <NotificationSystem currentUser={currentUser} />
              </div>
              
              <button
                onClick={handleLogout}
                className="bg-white/20 hover:bg-white/30 text-white px-2 sm:px-4 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-2 sm:px-6 lg:px-8">
        <div className="px-4 py-2 sm:px-0">
          {/* Welcome Section */}
          <div className="bg-white shadow rounded-lg pt-2 pb-4 px-6 mb-4">
            <div className="flex items-center space-x-4">
              {/* Profile Picture */}
              <div className="flex-shrink-0">
                <AvatarDisplay 
                  user={refreshedUser || {}}
                  size="xl"
                  alt="Profile"
                />
              </div>
              
              {/* Welcome Text */}
              <div className="flex-1">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1">
                  {refreshedUser?.nickname}'s Dashboard!
              </h2>
                <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-2">
                  {refreshedUser?.instrument} Player
                </p>
                
                {/* Teacher Linking Status */}
                <div className="flex items-center space-x-2">
                  {refreshedUser?.linkedTeachers && refreshedUser.linkedTeachers.length > 0 ? (
                    <div className="flex items-center space-x-2">
                      <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-700 font-medium text-xs sm:text-sm">
                        Linked to {linkedTeacherName || 'Teacher'}'s Teacher Account
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <svg className="h-5 w-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-yellow-700 font-medium">
                        Not linked to a teacher account
                    </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons - Compact Grid Layout */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Primary Actions - Larger */}
            <button
              onClick={() => navigate('/record-lesson')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white p-4 rounded-lg font-medium text-center transition-colors col-span-1"
            >
              <div className="text-xl mb-1">🎵</div>
              <div className="text-sm font-semibold">Take Lesson Notes</div>
            </button>
            
            <button
              onClick={handlePracticePlannerClick}
              className="bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-lg font-medium text-center transition-colors col-span-1"
            >
              <div className="text-xl mb-1">📝</div>
              <div className="text-sm font-semibold">Practice Planner</div>
            </button>
            
            <button
              onClick={() => navigate('/summaries')}
              className="bg-green-600 hover:bg-green-700 text-white p-4 rounded-lg font-medium text-center transition-colors col-span-1"
            >
              <div className="text-xl mb-1">📚</div>
              <div className="text-sm font-semibold">Lesson Summaries</div>
            </button>
            
            <button
              onClick={() => navigate('/analytics')}
              className="bg-orange-600 hover:bg-orange-700 text-white p-4 rounded-lg font-medium text-center transition-colors col-span-1"
            >
              <div className="text-xl mb-1">📊</div>
              <div className="text-sm font-semibold">Analytics</div>
            </button>
            
            <button
              onClick={() => navigate('/awards')}
              className="bg-yellow-600 hover:bg-yellow-700 text-white p-4 rounded-lg font-medium text-center transition-colors col-span-1"
            >
              <div className="text-xl mb-1">🏆</div>
              <div className="text-sm font-semibold">Awards</div>
            </button>
            
            <button
              onClick={() => navigate('/leaderboard')}
              className="bg-teal-600 hover:bg-teal-700 text-white p-4 rounded-lg font-medium text-center transition-colors col-span-1"
            >
              <div className="text-xl mb-1">🏅</div>
              <div className="text-sm font-semibold">Leaderboard</div>
            </button>
            
            <button
              onClick={() => navigate('/social')}
              className="bg-pink-600 hover:bg-pink-700 text-white p-4 rounded-lg font-medium text-center transition-colors col-span-1"
            >
              <div className="text-xl mb-1">👥</div>
              <div className="text-sm font-semibold">My Buddies</div>
            </button>
            
            <button
              onClick={() => navigate('/profile')}
              className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg font-medium text-center transition-colors col-span-1"
            >
              <div className="text-xl mb-1">👤</div>
              <div className="text-sm font-semibold">Profile</div>
            </button>
          </div>

          {/* Recent Feed Activity */}
          {currentUser?.role === 'student' && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                <button
                  onClick={() => navigate('/feed')}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center space-x-1"
                >
                  <span>View Full Feed</span>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              {loadingFeed ? (
                <div className="bg-white rounded-lg shadow-sm p-6 max-w-4xl mx-auto">
                <div className="text-center">
                    <div className="loading-spinner mb-2"></div>
                    <p className="text-gray-600">Loading recent activity...</p>
                </div>
                </div>
              ) : feedEvents.length > 0 ? (
                <div className="bg-white rounded-lg shadow-sm max-w-4xl mx-auto relative overflow-hidden">
                  {/* Carousel Container */}
                  <div className="relative">
                    {feedEvents.slice(0, 10).map((event, index) => (
                      <div
                        key={event.id}
                        className={`transition-all duration-500 ease-in-out ${
                          index === currentCarouselIndex
                            ? 'opacity-100 translate-x-0'
                            : 'opacity-0 absolute top-0 left-0 w-full'
                        }`}
                        style={{
                          transform: `translateX(${(index - currentCarouselIndex) * 100}%)`,
                          opacity: index === currentCarouselIndex ? 1 : 0
                        }}
                      >
                        <div className="bg-white rounded-lg shadow-sm py-4 px-6 hover:shadow-md transition-shadow">
                          {/* Desktop Layout - Horizontal */}
                          <div className="hidden sm:flex items-center space-x-4">
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-white font-bold text-sm">
                                  {event.fromUserNickname.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            
                            {/* Icon */}
                            <div className="flex-shrink-0">
                              {event.type === 'heart_sent' && <span className="text-xl">💖</span>}
                              {event.type === 'daily_goal_reached' && <span className="text-xl">🎯</span>}
                              {event.type === 'weekly_goal_reached' && <span className="text-xl">🏆</span>}
                              {event.type === 'badge_earned' && <span className="text-xl">🏅</span>}
                              {event.type === 'practice_reminder' && <span className="text-xl">⏰</span>}
                            </div>
                          
                            {/* Message */}
                            <div className="flex-1 min-w-0">
                              {event.type === 'heart_sent' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.fromUserNickname}</span> sent a heart to <span className="font-semibold">{event.toUserNickname}</span>
                                </span>
                              )}
                              {event.type === 'daily_goal_reached' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.fromUserNickname}</span> reached their daily goal!
                                </span>
                              )}
                              {event.type === 'weekly_goal_reached' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.fromUserNickname}</span> reached their weekly goal!
                                </span>
                              )}
                              {event.type === 'badge_earned' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.fromUserNickname}</span> earned {event.badgeName ? (
                                    <span className="font-semibold text-yellow-600">
                                      {(() => {
                                        const badgeDef = getBadgeDefinition(event.badgeName);
                                        
                                        return badgeDef ? (
                                          <span className="relative group">
                                            <span className="cursor-help underline decoration-dotted">"{event.badgeName}"</span>
                                            {/* Tooltip - positioned to the right */}
                                            <div className="absolute top-1/2 left-full transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                              {badgeDef.emoji} {badgeDef.description}
                                            </div>
                                          </span>
                                        ) : (
                                          <span className="text-orange-600" title={`No definition found for: ${event.badgeName}`}>
                                            "{event.badgeName}"
                                          </span>
                                        );
                                      })()}
                                    </span>
                                  ) : (
                                    'a new badge'
                                  )}!
                                </span>
                              )}
                              {event.type === 'practice_reminder' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.toUserNickname}</span> hasn't started their scheduled practice yet. Give them a nudge!
                                </span>
                              )}
                            </div>
                            
                            {/* Time */}
                            <div className="flex-shrink-0">
                              <span className="text-xs text-gray-500">
                                {formatTimestamp(event.timestamp)}
                              </span>
                            </div>
                            
                            {/* Action Button */}
                            <div className="flex-shrink-0">
                              {event.type === 'practice_reminder' ? (
                                usersStartedPractice.has(event.toUserId!) ? (
                                  <span className="text-green-600 text-xs font-medium px-2 py-1">
                                    ✅ Started Practice
                                  </span>
                                ) : (
                                  <div className="relative group">
                                    {(event.nudges && event.nudges.includes(currentUser?.uid || '')) || sentNudges.has(event.id) ? (
                                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                                        🤗 Nudge sent!
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => sendNudge(event.toUserId!, event.id)}
                                        className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-medium hover:bg-orange-200 transition-colors"
                                      >
                                        🤗 Nudge
                                      </button>
                                    )}
                                    {!((event.nudges && event.nudges.includes(currentUser?.uid || '')) || sentNudges.has(event.id)) && (
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                        Send your buddy a nudge to help keep them on target!
                                      </div>
                                    )}
                                  </div>
                                )
                              ) : event.fromUserId !== currentUser?.uid ? (
                                (event.hearts && event.hearts.includes(currentUser?.uid || '')) || sentHearts.has(event.id) ? (
                                  <span className="text-pink-600 text-xs font-medium px-2 py-1">
                                    💖 Heart sent!
                                  </span>
                                ) : (
                                  <div className="relative group">
                                    <button
                                      onClick={() => sendHeart(event.fromUserId, event.id)}
                                      className="bg-pink-100 text-pink-700 px-2 py-1 rounded text-xs font-medium hover:bg-pink-200 transition-colors"
                                    >
                                      💖 Heart
                                    </button>
                                    {/* Tooltip - positioned to the left */}
                                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                      Send your buddy a heart for their achievement
                                    </div>
                                  </div>
                                )
                              ) : (
                                <span className="text-gray-400 text-xs font-medium px-2 py-1">
                                  Your Achievement
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Mobile Layout - Two Lines */}
                          <div className="sm:hidden">
                            {/* Top Line - Message */}
                            <div className="mb-2">
                              {event.type === 'heart_sent' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.fromUserNickname}</span> sent a heart to <span className="font-semibold">{event.toUserNickname}</span>
                                </span>
                              )}
                              {event.type === 'daily_goal_reached' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.fromUserNickname}</span> reached their daily goal!
                                </span>
                              )}
                              {event.type === 'weekly_goal_reached' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.fromUserNickname}</span> reached their weekly goal!
                                </span>
                              )}
                              {event.type === 'badge_earned' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.fromUserNickname}</span> earned {event.badgeName ? (
                                    <span className="font-semibold text-yellow-600">
                                      {(() => {
                                        const badgeDef = getBadgeDefinition(event.badgeName);
                                        
                                        return badgeDef ? (
                                          <span className="relative group">
                                            <span className="cursor-help underline decoration-dotted">"{event.badgeName}"</span>
                                            {/* Tooltip - positioned to the right */}
                                            <div className="absolute top-1/2 left-full transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                              {badgeDef.emoji} {badgeDef.description}
                                            </div>
                                          </span>
                                        ) : (
                                          <span className="text-orange-600" title={`No definition found for: ${event.badgeName}`}>
                                            "{event.badgeName}"
                                          </span>
                                        );
                                      })()}
                                    </span>
                                  ) : (
                                    'a new badge'
                                  )}!
                                </span>
                              )}
                              {event.type === 'practice_reminder' && (
                                <span className="text-sm text-gray-700">
                                  <span className="font-semibold">{event.toUserNickname}</span> hasn't started their scheduled practice yet. Give them a nudge!
                                </span>
                              )}
                            </div>

                            {/* Bottom Line - Avatar, Icon, Time, Action Button */}
                            <div className="flex items-center space-x-3">
                              {/* Avatar */}
                              <div className="flex-shrink-0">
                                <div className="w-6 h-6 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                                  <span className="text-white font-bold text-xs">
                                    {event.fromUserNickname.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Icon */}
                              <div className="flex-shrink-0">
                                {event.type === 'heart_sent' && <span className="text-sm">💖</span>}
                                {event.type === 'daily_goal_reached' && <span className="text-sm">🎯</span>}
                                {event.type === 'weekly_goal_reached' && <span className="text-sm">🏆</span>}
                                {event.type === 'badge_earned' && <span className="text-sm">🏅</span>}
                                {event.type === 'practice_reminder' && <span className="text-sm">⏰</span>}
                              </div>
                              
                              {/* Time */}
                              <div className="flex-1">
                                <span className="text-xs text-gray-500">
                                  {formatTimestamp(event.timestamp)}
                                </span>
                              </div>
                              
                              {/* Action Button */}
                              <div className="flex-shrink-0">
                                {event.type === 'practice_reminder' ? (
                                  usersStartedPractice.has(event.toUserId!) ? (
                                    <span className="text-green-600 text-xs font-medium px-2 py-1">
                                      ✅ Started
                                    </span>
                                  ) : (
                                    <div className="relative group">
                                      {(event.nudges && event.nudges.includes(currentUser?.uid || '')) || sentNudges.has(event.id) ? (
                                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                                          🤗 Sent
                                        </span>
                                      ) : (
                                        <button
                                          onClick={() => sendNudge(event.toUserId!, event.id)}
                                          className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-medium hover:bg-orange-200 transition-colors"
                                        >
                                          🤗 Nudge
                                        </button>
                                      )}
                                      {!((event.nudges && event.nudges.includes(currentUser?.uid || '')) || sentNudges.has(event.id)) && (
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                          Send your buddy a nudge to help keep them on target!
                                        </div>
                                      )}
                                    </div>
                                  )
                                ) : event.fromUserId !== currentUser?.uid ? (
                                  (event.hearts && event.hearts.includes(currentUser?.uid || '')) || sentHearts.has(event.id) ? (
                                    <span className="text-pink-600 text-xs font-medium px-2 py-1">
                                      💖 Sent
                                    </span>
                                  ) : (
                                    <div className="relative group">
                                      <button
                                        onClick={() => sendHeart(event.fromUserId, event.id)}
                                        className="bg-pink-100 text-pink-700 px-2 py-1 rounded text-xs font-medium hover:bg-pink-200 transition-colors"
                                      >
                                        💖 Heart
                                      </button>
                                      {/* Tooltip - positioned to the left */}
                                      <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                        Send your buddy a heart for their achievement
                                      </div>
                                    </div>
                                  )
                                ) : (
                                  <span className="text-gray-400 text-xs font-medium px-2 py-1">
                                    Your Achievement
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Navigation Controls */}
                  {feedEvents.length > 1 && (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-b-lg">
                      <button
                        onClick={goToPreviousCard}
                        className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="text-sm font-medium">Previous</span>
                      </button>
                      
                      {/* Dots Indicator */}
                      <div className="flex space-x-2">
                        {feedEvents.slice(0, 10).map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentCarouselIndex(index)}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              index === currentCarouselIndex ? 'bg-indigo-600' : 'bg-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      
                      <button
                        onClick={goToNextCard}
                        className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <span className="text-sm font-medium">Next</span>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg shadow-sm p-6 max-w-4xl mx-auto border border-indigo-100">
                <div className="text-center">
                    <div className="text-indigo-600 text-4xl mb-4">🏠</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No Recent Activity
                  </h3>
                <p className="text-gray-600">
                      Add some practice buddies to see their activity here, or start practicing to generate your own activity!
                </p>
                </div>
              </div>
              )}
            </div>
          )}

          {/* Daily Practice Goal Card */}
          {currentUser?.role === 'student' && (
            <div className="mt-4">
              <DailyPracticeGoalCard
                currentUser={currentUser}
                badgesLoaded={badgesLoaded}
                earnedBadges={earnedBadges}
                isPracticeSessionActive={isPracticeSessionActive}
                currentSessionProgress={currentSessionProgress}
                showResetButton={false}
              />
                    </div>
          )}

        </div>
      </main>

      {/* Birthday Celebration Popup */}
      <BirthdayCelebration
        isOpen={showBirthdayCelebration}
        onClose={handleBirthdayCelebrationClose}
        isBadgeEarned={hasBirthdayBadge}
      />

      {/* Badge Celebration Popup */}
      {showBadgeCelebration && newBadge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl animate-pulse">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Congratulations!</h2>
            <div className="text-4xl mb-4">{newBadge.emoji}</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">{newBadge.name}</h3>
            <p className="text-gray-600 mb-6">{newBadge.description}</p>
            <button
              onClick={() => setShowBadgeCelebration(false)}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Awesome!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
